import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import { useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import {
	getIssueLabel,
	IssueTypeEnum,
	SubmitFeedbackSchema,
	type FeedbackVariant,
} from '#app/routes/_marketing+/feedback+/feedback-schema.ts'
import { Field, SelectField, TextareaField } from '../forms.tsx'
import { StatusButton } from '../ui/status-button.tsx'

export interface FeedbackFormProps {
	variant: FeedbackVariant
	defaultValues?: {
		email?: string
		businessId?: string
		businessName?: string
		pageUrl?: string
	}
	onSuccess?: () => void
}

export function FeedbackForm({
	variant,
	defaultValues,
	onSuccess,
}: FeedbackFormProps) {
	const fetcher = useFetcher<{ success: boolean; message: string }>()

	const [form, fields] = useForm({
		id: `feedback-form-${variant}`,
		constraint: getZodConstraint(SubmitFeedbackSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: SubmitFeedbackSchema })
		},
		defaultValue: {
			variant,
			email: defaultValues?.email || '',
			businessId: defaultValues?.businessId || '',
			businessName: defaultValues?.businessName || '',
			pageUrl: defaultValues?.pageUrl || '',
		},
		shouldRevalidate: 'onBlur',
	})

	const isPending = fetcher.state !== 'idle'
	const isSuccess = fetcher.data?.success === true
	const errorMessage =
		fetcher.data?.success === false ? fetcher.data.message : null

	// Call onSuccess callback when submission succeeds
	if (isSuccess && onSuccess && fetcher.data) {
		// Use a small timeout to allow the UI to show success state
		setTimeout(onSuccess, 1500)
	}

	return (
		<fetcher.Form
			method="post"
			action="/resources/feedback"
			{...getFormProps(form)}
		>
			<HoneypotInputs />

			{/* Hidden fields for variant and context */}
			<input type="hidden" name="variant" value={variant} />
			{defaultValues?.businessId && (
				<input
					type="hidden"
					name="businessId"
					value={defaultValues.businessId}
				/>
			)}
			{defaultValues?.businessName && (
				<input
					type="hidden"
					name="businessName"
					value={defaultValues.businessName}
				/>
			)}
			{defaultValues?.pageUrl && (
				<input type="hidden" name="pageUrl" value={defaultValues.pageUrl} />
			)}

			<div>
				{/* Email field - always shown */}
				<Field
					labelProps={{ children: 'Your Email' }}
					inputProps={{
						...getInputProps(fields.email, { type: 'email' }),
						placeholder: 'you@example.com',
						autoComplete: 'email',
					}}
					errors={fields.email.errors}
				/>

				{/* Issue type - only for flag variant */}
				{variant === 'flag' && (
					<SelectField
						labelProps={{ children: 'Issue Type' }}
						selectProps={{
							...getInputProps(fields.issue, { type: 'text' }),
							placeholder: 'Select an issue type...',
						}}
						selectOptions={IssueTypeEnum.options.map((issue) => ({
							value: issue,
							label: getIssueLabel(issue),
						}))}
						field={fields.issue}
						errors={fields.issue.errors}
					/>
				)}

				{/* Message field */}
				<TextareaField
					labelProps={{
						children:
							variant === 'contact'
								? 'Message'
								: 'Additional Details (Optional)',
					}}
					textareaProps={{
						...getInputProps(fields.message, { type: 'text' }),
						placeholder: getMessagePlaceholder(variant),
						rows: 5,
					}}
					errors={fields.message.errors}
				/>

				{/* Status message */}
				{isSuccess && fetcher.data && (
					<div className="text-success mb-4 rounded-md bg-green-50 p-3 text-sm">
						{fetcher.data.message}
					</div>
				)}

				{errorMessage && (
					<div className="text-destructive mb-4 rounded-md bg-red-50 p-3 text-sm">
						{errorMessage}
					</div>
				)}

				{/* Submit button */}
				<StatusButton
					type="submit"
					className="w-full"
					status={isPending ? 'pending' : isSuccess ? 'success' : 'idle'}
					disabled={isPending || isSuccess}
				>
					{isSuccess ? 'Submitted!' : getSubmitButtonText(variant)}
				</StatusButton>
			</div>
		</fetcher.Form>
	)
}

function getMessagePlaceholder(variant: FeedbackVariant): string {
	switch (variant) {
		case 'flag':
			return 'Provide additional details about the issue...'
		case 'feedback':
			return 'Share your feedback with us...'
		case 'contact':
			return 'Your message...'
		default:
			return 'Your message...'
	}
}

function getSubmitButtonText(variant: FeedbackVariant): string {
	switch (variant) {
		case 'flag':
			return 'Submit Report'
		case 'feedback':
			return 'Send Feedback'
		case 'contact':
			return 'Send Message'
		default:
			return 'Submit'
	}
}
